import {
  useState,
  createContext,
  useRef,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { useFm, useToast, useHybirdWallet, useParams } from '@web3/base-hooks';
import { LoginStatus, UserStatus } from '@/types/user';
import { WalletType } from '@web3/base-utils';
import {
  getRegisterStatus,
  getRegister,
  getBinding,
  getSign,
} from '@/services/user';
import { RegisterStatus, GetRegisterStatusParams } from '@/types/user';
import { useErrorCodes } from '@/hooks/errorCodes';
import { useSwitchWallet } from '@/hooks/useSwitchWallet';
import { muteEmail, shortenString } from '@/utils';

export type UserCommonParams = {
  userId?: string;
  walletId?: string;
  walletType?: WalletType;
};

export const UserStatusContext = createContext<{
  email: string;
  address: string;
  loginStatus: LoginStatus;
  userStatus: UserStatus;
  userCommonParams?: UserCommonParams;
  isBindingLoading: boolean;
  isRegisterLoading: boolean;
  getUserCommonParams: (selfUserStatus?: UserStatus) => UserCommonParams;
  beginRegister: () => Promise<boolean>;
  beginBinding: () => Promise<boolean>;
}>({
  email: '',
  address: '',
  loginStatus: LoginStatus.NOT_READY,
  userStatus: UserStatus.NOT_READY,
  userCommonParams: {},
  isBindingLoading: false,
  isRegisterLoading: false,
  getUserCommonParams: (selfUserStatus: UserStatus) => {
    return {};
  },
  beginRegister: () => Promise.resolve(true),
  beginBinding: () => Promise.resolve(true),
});

UserStatusContext.displayName = 'UserStatusContext';
interface Props {
  children: ReactNode;
}

export const UserStatusProvider = (props: Props) => {
  const { children } = props;
  const fm = useFm();

  const [userStatus, setUserStatus] = useState(UserStatus.NOT_READY);
  const [userCommonParams, setUserCommonParams] = useState<UserCommonParams>();
  const [isBindingLoading, setIsBindingLoading] = useState(false);
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const { locale } = useParams();

  const spotRegisterInfo = useRef({
    id: '',
    registerStatus: RegisterStatus.NOT,
    bindId: '',
  });
  const walletRegisterInfo = useRef({
    id: '',
    registerStatus: RegisterStatus.NOT,
    bindId: '',
  });
  const { openSwitchWalletModal } = useSwitchWallet();

  const {
    walletIsReady,
    isLogin,
    walletId,
    walletType,
    evmWalletInfo,
    userInfo,
    evmSign,
  } = useHybirdWallet();

  const { queryErrorMsg } = useErrorCodes();

  const loginStatus = useMemo(() => {
    if (!walletIsReady) return LoginStatus.NOT_READY;

    if (!isLogin) return LoginStatus.NONE;

    if (
      walletId &&
      walletType === WalletType.MNEMONIC_PHRASE &&
      userInfo.userId
    )
      return LoginStatus.BOTH;

    if (userInfo.userId) return LoginStatus.ONLY_SPOT;

    if (walletId && walletType === WalletType.MNEMONIC_PHRASE)
      return LoginStatus.ONLY_WALLET;

    if (walletId && walletType !== WalletType.MNEMONIC_PHRASE)
      return LoginStatus.NOT_TARGET_TYPE;
  }, [walletIsReady, isLogin, walletId, walletType, userInfo.userId]);

  const getUserStatus = () => {
    if (
      [
        LoginStatus.NOT_READY,
        LoginStatus.NONE,
        LoginStatus.NOT_TARGET_TYPE,
      ].includes(loginStatus)
    ) {
      return UserStatus.NOT_READY;
    }

    const getWalletUserStatus = () => {
      if (
        walletRegisterInfo.current.id &&
        walletRegisterInfo.current.id === walletId
      ) {
        if (walletRegisterInfo.current.registerStatus === RegisterStatus.YES) {
          if (walletRegisterInfo.current.bindId) {
            return UserStatus.WALLET_FINISH;
          } else {
            return UserStatus.SPOT_NOT_BINDING;
          }
        }

        if (walletRegisterInfo.current.registerStatus === RegisterStatus.NOT) {
          return UserStatus.NOT_REGISTER;
        }
      }
      return UserStatus.NOT_READY;
    };

    const getSpotUserStatus = () => {
      if (
        spotRegisterInfo.current.id &&
        spotRegisterInfo.current.id === userInfo.userId
      ) {
        if (spotRegisterInfo.current.registerStatus === RegisterStatus.YES) {
          if (spotRegisterInfo.current.bindId) {
            return UserStatus.SPOT_FINISH;
          } else {
            return UserStatus.WALLET_NOT_BINDING;
          }
        }

        if (spotRegisterInfo.current.registerStatus === RegisterStatus.NOT) {
          return UserStatus.NOT_REGISTER;
        }
      }

      return UserStatus.NOT_READY;
    };

    if (loginStatus === LoginStatus.BOTH) {
      const walletUserStatus = getWalletUserStatus();
      const spotUserStatus = getSpotUserStatus();

      if (walletUserStatus === UserStatus.NOT_REGISTER) {
        return spotUserStatus;
      }

      return walletUserStatus;
    }

    if (loginStatus === LoginStatus.ONLY_SPOT) return getSpotUserStatus();

    if (loginStatus === LoginStatus.ONLY_WALLET) return getWalletUserStatus();
  };

  const { toastWarn, toastSuccess, toastFail } = useToast();
  const getUserCommonParams = (selfUserStatus: UserStatus = userStatus) => {
    if (
      selfUserStatus === UserStatus.SPOT_NOT_BINDING ||
      selfUserStatus === UserStatus.WALLET_FINISH
    ) {
      return {
        walletId,
        walletType,
      };
    }

    if (
      selfUserStatus === UserStatus.WALLET_NOT_BINDING ||
      selfUserStatus === UserStatus.SPOT_FINISH
    ) {
      return {
        userId: userInfo.userId,
      };
    }

    return walletId
      ? {
          walletId,
          walletType,
        }
      : {
          userId: userInfo.userId,
        };
  };

  const beginRegister = async () => {
    setIsRegisterLoading(true);
    const { success, result, code } = await getRegister(
      loginStatus === LoginStatus.ONLY_SPOT
        ? {
            userId: userInfo.userId,
            email: userInfo.email,
          }
        : {
            walletId,
            walletType,
            walletAddress: evmWalletInfo.address,
          }
    );

    if (!success) {
      //
      toastWarn(queryErrorMsg(code));
      await initUserStatus();
      setIsRegisterLoading(false);
      return;
    }

    await initUserStatus();
    setIsRegisterLoading(false);

    return true;
  };

  const beginBinding = async () => {
    if (walletType !== WalletType.MNEMONIC_PHRASE) {
      openSwitchWalletModal();
      return;
    }

    setIsBindingLoading(true);
    const {
      success: signSuccess,
      result: { message },
      code: signCode,
    } = await getSign({
      chainCode: 'ETH',
      walletAddress: evmWalletInfo.address,
      language: locale,
    });

    if (!signSuccess) {
      toastWarn(queryErrorMsg(signCode));
      setIsBindingLoading(false);
    }

    let signedData;

    try {
      signedData = await evmSign(message);
    } catch (e) {
      setIsBindingLoading(false);
      return;
    }

    if (!signedData) {
      setIsBindingLoading(false);
      return;
    }

    // toastWarn(signedData);

    const { success, result, code } = await getBinding({
      walletId,
      walletType,
      walletAddress: evmWalletInfo.address,
      userId: userInfo.userId,
      email: userInfo.email,
      chainCode: 'ETH',
      message,
      sign: signedData,
    });

    if (!success) {
      toastWarn(queryErrorMsg(code));
      await initUserStatus();
      setIsBindingLoading(false);
      return;
    }

    await initUserStatus();
    setIsBindingLoading(false);
    return true;
  };

  const initUserStatus = async () => {
    // 没登录的情况
    if ([LoginStatus.NOT_READY].includes(loginStatus)) {
      return;
    }

    if ([LoginStatus.NONE, LoginStatus.NOT_TARGET_TYPE].includes(loginStatus)) {
      setUserStatus(getUserStatus());
      setUserCommonParams({});
      return;
    }

    if (loginStatus === LoginStatus.BOTH) {
      const [result1, result2] = await Promise.all([
        getRegisterStatus({
          walletId,
          walletType,
        }),
        getRegisterStatus({
          userId: userInfo.userId,
        }),
      ]);

      if (!result1.success || !result2.success) {
        toastWarn(
          queryErrorMsg(result1.code === 0 ? result2.code : result1.code)
        );
        return;
      }

      walletRegisterInfo.current = {
        id: walletId,
        registerStatus: result1.result.registerStatus,
        bindId: result1.result.bindId,
      };

      spotRegisterInfo.current = {
        id: userInfo.userId,
        registerStatus: result2.result.registerStatus,
        bindId: result2.result.bindId,
      };
    }

    if (loginStatus === LoginStatus.ONLY_WALLET) {
      const { success, result, code } = await getRegisterStatus({
        walletId,
        walletType,
      });
      if (!success) {
        toastWarn(queryErrorMsg(code));
        return;
      }

      walletRegisterInfo.current = {
        id: walletId,
        registerStatus: result.registerStatus,
        bindId: result.bindId,
      };
    }

    if (loginStatus === LoginStatus.ONLY_SPOT) {
      const { success, result, code } = await getRegisterStatus({
        userId: userInfo.userId,
      });
      if (!success) {
        toastWarn(queryErrorMsg(code));
        return;
      }

      spotRegisterInfo.current = {
        id: userInfo.userId,
        registerStatus: result.registerStatus,
        bindId: result.bindId,
      };
    }

    const newUserStatus = getUserStatus();

    const newUserCommonParams = getUserCommonParams(newUserStatus);
    setUserStatus(newUserStatus);
    setUserCommonParams(newUserCommonParams);

    if (userCommonParams) {
      if (newUserCommonParams.userId && !userInfo.email) return;
      toastSuccess(
        fm('switchedToAddress', {
          address: newUserCommonParams.userId
            ? muteEmail(userInfo.email)
            : shortenString(evmWalletInfo.address),
        })
      );
    }
  };

  useEffect(() => {
    if (!walletIsReady) return;
    initUserStatus();
  }, [walletId, userInfo.userId, walletIsReady]);

  useEffect(() => {
    if (userStatus === UserStatus.NOT_REGISTER) {
      toastFail(fm('yetToRegister'));
    }
  }, [userStatus]);

  return (
    <UserStatusContext.Provider
      value={{
        // 由于钱包账号优先于email使用，所以对于email来说，bindId优先级最高
        email:
          userStatus === UserStatus.WALLET_FINISH
            ? walletRegisterInfo.current.bindId
            : userInfo.email,
        address:
          userStatus === UserStatus.SPOT_FINISH
            ? spotRegisterInfo.current.bindId
            : evmWalletInfo.address,
        userStatus,
        loginStatus,
        userCommonParams,
        isRegisterLoading,
        isBindingLoading,
        getUserCommonParams,
        beginRegister,
        beginBinding,
      }}
    >
      {children}
    </UserStatusContext.Provider>
  );
};
